import { environment } from '../../../environments/environment';

declare const mapboxgl: typeof import('mapbox-gl');
declare const MapboxGeocoder: typeof import('@mapbox/mapbox-gl-geocoder');

/**
 * On-demand loader for Mapbox GL JS and the geocoder plugin.
 *
 * These used to be two `defer` <script> tags in `src/index.html`. That cost
 * 245 KB over the wire on *every* page view, map or not - but the bigger
 * problem was ordering: classic `defer` scripts and `type=module` scripts
 * share one in-order deferred execution list, ordered by document position.
 * mapbox-gl.js sat in <head> and main.js at the end of <body>, so ~800 KB of
 * Mapbox had to compile on the main thread before Angular could bootstrap -
 * on `/sales-forecast/*`, where the paid traffic lands and no map exists at
 * all. See `todos/optimization.md`, item 4.
 *
 * Plain module functions rather than an Angular service on purpose: this lets
 * `app-routing.module.ts` fire `preloadMapbox()` from inside `loadComponent`,
 * so the CDN fetch starts at the same instant as the route chunk instead of
 * waiting behind it.
 *
 * Still loaded from api.mapbox.com rather than bundled from npm: there is no
 * CDN in front of our origin yet (optimization.md item 3), so a self-hosted
 * copy would come from one EC2 box in Virginia instead of a Mapbox edge with
 * brotli. Worth revisiting once item 3 lands.
 */

const GL_VERSION = 'v2.15.0';
const GEOCODER_VERSION = 'v5.0.0';

const GL_JS = `https://api.mapbox.com/mapbox-gl-js/${GL_VERSION}/mapbox-gl.js`;
const GL_CSS = `https://api.mapbox.com/mapbox-gl-js/${GL_VERSION}/mapbox-gl.css`;
const GEOCODER_JS = `https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-geocoder/${GEOCODER_VERSION}/mapbox-gl-geocoder.min.js`;
const GEOCODER_CSS = `https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-geocoder/${GEOCODER_VERSION}/mapbox-gl-geocoder.css`;

/**
 * Per attempt, not for the whole load. Long enough not to abandon a slow but
 * working connection, short enough that three attempts still resolve inside
 * about half a minute. A timeout does not cancel the `<script>` it gave up on,
 * so `recoverLateArrival()` checks whether it landed anyway before the next
 * attempt spends anything.
 */
const ATTEMPT_TIMEOUT_MS = 10_000;

/** Delay before attempts 2 and 3. Its length is also the attempt count. */
const RETRY_BACKOFF_MS = [500, 2_000];

/**
 * Thrown when a bundle loaded but did not define what it should - a corrupted
 * CDN response, or a captive portal answering 200 with its own HTML. Retrying
 * *immediately* cannot fix that, so it skips the backoff and fails at once.
 *
 * It does still drop the asset from the cache on the way out (see
 * `forgetAsset`): the request itself succeeded, so without that the resolved
 * entry would be reused forever and every later attempt - the retry button,
 * the `online` handler - would short-circuit to the same error without ever
 * asking the network again.
 */
class MapboxContractError extends Error {}

/**
 * The single error a give-up reports, wrapping whatever the last attempt threw.
 *
 * Sentry's `captureConsoleIntegration` titles an event after the first `Error`
 * among a console.error's arguments and discards the string it was logged
 * with, so handing it the last attempt's rejection filed these failures as
 * "[mapbox] mapbox-gl.js did not load within 10000ms": a per-attempt timeout,
 * indistinguishable from one that attempt 2 went on to recover from, and
 * silent about how long the load really had. This wrapper says the load is
 * over as a whole and keeps its message constant so the issue groups; `cause`
 * carries the attempt's own error, which Sentry's linkedErrors integration
 * follows and shows as a chained exception, and the counts go in the console
 * line, which arrives as the event's `arguments` extra.
 */
class MapboxLoadFailure extends Error {
  constructor(
    readonly what: string,
    readonly attempts: number,
    readonly elapsedMs: number,
    cause: unknown
  ) {
    super(`[mapbox] ${what} failed to load after every attempt`, { cause });
    this.name = 'MapboxLoadFailure';
  }
}

/** "3 attempts over 33.1s", for the console line beside a failure. */
function describeAttempts(error: unknown): string {
  if (!(error instanceof MapboxLoadFailure)) return 'every attempt';
  const plural = error.attempts === 1 ? '' : 's';
  const seconds = Math.round(error.elapsedMs / 100) / 10;
  return `${error.attempts} attempt${plural} over ${seconds}s`;
}

/** Failures already logged at error level, and so already sent to Sentry. */
const reportedFailures = new WeakSet<object>();

/**
 * console.error the first time a failure is reported, console.warn after that.
 *
 * `loadMapboxGeocoder()` awaits `loadMapboxGl()`, so one blocked CDN rejects
 * both and every catch on the way out logs the same error - two Sentry events
 * for a single failed load, more on a page that asks for both. The console
 * still says everything, a dead address search being worth seeing next to a
 * dead map; only the duplicate report is dropped, `captureConsoleIntegration`
 * being configured for the error level alone.
 */
function reportLoadFailure(message: string, error: unknown): void {
  const trackable = typeof error === 'object' && error !== null;
  const duplicate = trackable && reportedFailures.has(error);
  if (trackable) reportedFailures.add(error);
  (duplicate ? console.warn : console.error)(message, error);
}

let glPromise: Promise<typeof mapboxgl> | null = null;
let geocoderPromise: Promise<typeof MapboxGeocoder> | null = null;

/** True while a cached rejection is the only thing callers would get back. */
let hasFailed = false;

type RetryListener = () => void;
const retryListeners = new Set<RetryListener>();

let onlineRecoveryInstalled = false;

/**
 * One promise per URL, so a bundle already on the page is never re-requested
 * and concurrent callers share one request.
 *
 * The invariant this map exists to keep: **an asset is requested again only
 * after a request for it actually failed.** A `<script>` cannot be recalled
 * once it is in flight - removing the tag neither cancels the download nor
 * stops the bundle executing - so a second request for a still-pending URL
 * means two copies of GL JS run and the later one overwrites
 * `window.mapboxgl`. Everything the first copy built, a live `Map` included,
 * then belongs to a class the new global no longer matches:
 * `LngLatBounds.convert` sees `input instanceof LngLatBounds` fail, rebuilds
 * it through the `[sw, ne]` branch, and `fitBounds` dies on "`LngLatLike`
 * argument must be specified as ...". Only a request that *settled* as a
 * failure may be retried; a slow one is waited on. See `withTimeout`.
 *
 * A *failed* entry is deleted rather than kept. That matters more than it
 * looks: an earlier version of this file looked for an existing `<script>` tag
 * and attached a `load` listener to it, which never fires if the tag had
 * already finished - so any retry would have hung until its own timeout.
 */
const assetPromises = new Map<string, Promise<void>>();

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Drops a cached asset so the next attempt issues a real request for it. */
function forgetAsset(url: string): void {
  assetPromises.delete(url);
}

function loadScript(src: string): Promise<void> {
  const cached = assetPromises.get(src);
  if (cached) return cached;

  const work = new Promise<void>((resolve, reject) => {
    // Any tag here is left over from a request that errored, or from a bundle
    // that answered with something other than itself - a pending one keeps its
    // cache entry, so this function is never reached while one is in flight.
    document
      .querySelectorAll(`script[src="${src}"]`)
      .forEach((el) => el.remove());

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });

  assetPromises.set(src, work);
  // Delete only our *own* entry: a contract failure may have dropped this key
  // and let a later caller store its own live promise under it before this
  // request errors. An unguarded delete would evict that live entry, sending
  // the next caller back into `loadScript` for a second copy of the same
  // 800 KB - the thing `assetPromises` exists to prevent.
  work.catch(() => {
    if (assetPromises.get(src) === work) assetPromises.delete(src);
  });
  return work;
}

/**
 * Awaited rather than fire-and-forget: mapbox-gl.css positions the canvas and
 * the controls, so building a map before it lands gives a frame of overflowing
 * canvas and unstyled controls.
 *
 * A failure here resolves anyway - a missing stylesheet is cosmetic, and
 * failing the whole load over it would cost the visitor the map as well. The
 * cache entry is still dropped, so the next attempt re-tries it.
 */
function loadStylesheet(href: string): Promise<void> {
  const cached = assetPromises.get(href);
  if (cached) return cached;

  const work: Promise<void> = new Promise((resolve) => {
    document
      .querySelectorAll(`link[href="${href}"]`)
      .forEach((el) => el.remove());

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.crossOrigin = 'anonymous';
    link.onload = () => resolve();
    link.onerror = () => {
      // console.warn and not console.error: this resolves anyway, so it costs
      // the visitor nothing, and captureConsoleIntegration reports the error
      // level - see `reportLoadFailure`.
      console.warn(`[mapbox] failed to load stylesheet ${href}`);
      // Guarded for the same reason as in `loadScript`: by now a later attempt
      // may own this key, and evicting its entry would re-request the asset.
      if (assetPromises.get(href) === work) assetPromises.delete(href);
      resolve();
    };
    document.head.appendChild(link);
  });

  assetPromises.set(href, work);
  return work;
}

/**
 * Fails an attempt without touching what it was waiting for.
 *
 * A timeout used to drop the pending assets from the cache so the next attempt
 * would issue a real request rather than re-await a promise that might never
 * settle. That bought a second request for a bundle whose first request was
 * still in flight, and both copies ran - see `assetPromises` for what that
 * costs. So a timed-out request is now waited on rather than replaced: for a
 * stalled download the three attempts are one request under a ~32 s budget,
 * with `recoverLateArrival()` between them, and a retry that reaches the
 * network is reserved for a request that genuinely errored - which
 * `loadScript` already drops from the cache, and which never executed anything
 * to conflict with.
 */
function withTimeout<T>(work: Promise<T>, what: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(
        new Error(
          `[mapbox] ${what} did not load within ${ATTEMPT_TIMEOUT_MS}ms`
        )
      );
    }, ATTEMPT_TIMEOUT_MS);

    work.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });
}

/**
 * A timed-out attempt abandons its `<script>` but cannot cancel it, so the
 * bundle may have arrived a moment later and be sitting on `window` perfectly
 * usable. Without this check the visitor keeps an error state over a working
 * library.
 */
function recoverLateArrival<T>(globalName: string): T | null {
  const value = (window as any)[globalName] as T | undefined;
  if (value) {
    console.warn(
      `[mapbox] ${globalName} arrived after its attempt had given up; using it`
    );
    // The asset's own promise has resolved by now - the global cannot exist
    // without the script having run - so the cache entry is correct as it
    // stands. Dropping it here used to look like tidying up; what it actually
    // did was let the retry button ask for a second copy of a bundle that was
    // already on the page.
    return value;
  }
  return null;
}

/**
 * Runs `attempt` up to `RETRY_BACKOFF_MS.length + 1` times.
 *
 * A dropped request for a 220 KB asset on a phone is the common failure here
 * and a retry fixes it, which is why this exists at all. It is a different
 * trade from the gtag retry that `optimization.md` removed: a lost analytics
 * event costs nothing, whereas `/` with no Mapbox is not a page.
 */
async function withRetry<T>(
  attempt: () => Promise<T>,
  what: string,
  globalName: string
): Promise<T> {
  const startedAt = Date.now();
  let lastError: unknown;
  let attempts = 0;

  for (let i = 0; i <= RETRY_BACKOFF_MS.length; i++) {
    attempts++;
    try {
      return await attempt();
    } catch (error) {
      lastError = error;

      const late = recoverLateArrival<T>(globalName);
      if (late) return late;

      // The bytes arrived and were not what they should be. Asking again for
      // the same URL will produce the same answer.
      if (error instanceof MapboxContractError) break;

      const backoff = RETRY_BACKOFF_MS[i];
      if (backoff === undefined) break;

      console.warn(
        `[mapbox] ${what} attempt ${i + 1} failed, retrying in ${backoff}ms`,
        error
      );
      await delay(backoff);
    }
  }

  throw new MapboxLoadFailure(
    what,
    attempts,
    Date.now() - startedAt,
    lastError
  );
}

/**
 * Reinstates the network-recovery listener. Installed on first use rather than
 * at module load so a page that never touches a map never adds a listener.
 */
function installOnlineRecovery(): void {
  if (onlineRecoveryInstalled || typeof window === 'undefined') return;
  onlineRecoveryInstalled = true;

  window.addEventListener('online', () => {
    if (!hasFailed) return;

    console.warn(
      '[mapbox] connection is back after a failed load; inviting another attempt'
    );
    resetMapboxLoadState();

    for (const listener of retryListeners) {
      try {
        listener();
      } catch (error) {
        console.error('[mapbox] a retry listener threw', error);
      }
    }
  });
}

/**
 * Clears cached rejections so the next `loadMapboxGl()`/`loadMapboxGeocoder()`
 * really tries again.
 *
 * Rejections used to be cached on the reasoning that a blocked CDN stays
 * blocked. That is true of an ad blocker and false of everything else, and
 * api.mapbox.com is a library CDN rather than a tracker host - so the blocked
 * case is the rare one and a transient failure was being made permanent.
 *
 * Successful loads are untouched: their entries in `assetPromises` are already
 * resolved, so a reset costs one microtask rather than a second download.
 *
 * A no-op when nothing has failed, which is what keeps concurrent callers from
 * tripping over each other. `CustomRouteReuseStrategy` retains the map route,
 * so a detached `MapboxComponent` survives a navigation with its
 * `onMapboxRetryAvailable` listener still registered - one `online` event then
 * reaches two live subscribers, and each calls this. Without the guard the
 * second reset would null out the first one's in-flight promise, orphaning it:
 * a third caller would start yet another load, and the orphan's `.catch` would
 * later set `hasFailed` again even if the live attempt had succeeded.
 */
export function resetMapboxLoadState(): void {
  if (!hasFailed) return;
  glPromise = null;
  geocoderPromise = null;
  hasFailed = false;
}

/**
 * Registers a callback for "it is worth trying again now" - currently the
 * `online` event after a failure. Returns its own unsubscribe.
 */
export function onMapboxRetryAvailable(listener: RetryListener): () => void {
  retryListeners.add(listener);
  return () => {
    retryListeners.delete(listener);
  };
}

/**
 * The answer to "load Mapbox" in a context that has no browser to load it into.
 *
 * `@angular/platform-server` installs domino's DOM *classes* and nothing else,
 * so `document` and `window` are both undefined during a server render. The
 * attempt therefore never reaches the CDN: `loadScript` throws on its first
 * line, and the retry ladder above then waits out 500ms + 2000ms of timers
 * that hold a prerender open to reach the same answer three times.
 *
 * Deliberately unsettled rather than rejected. Every caller's `.catch` puts a
 * "the map could not be loaded" message on screen, and under a prerender that
 * message is what gets serialized into the static HTML a visitor reads before
 * bootstrap. Leaving the promise pending keeps the map slot in its initial
 * state instead, which is what a client-only map is supposed to look like in
 * prerendered output.
 *
 * It holds nothing open: an unsettled promise schedules no task, so the zone
 * still falls quiet and the render is serialized at once. The server's module
 * instance is discarded afterwards, so this is never the promise a browser
 * gets.
 */
const NEVER_IN_THIS_CONTEXT: Promise<never> = new Promise(() => {});

/**
 * Resolves with the global `mapboxgl`, with `accessToken` already set.
 *
 * Idempotent and single-flight: concurrent callers share one promise. On
 * failure the cached promise is dropped, so a later caller - a retry button, a
 * second map, the `online` handler - starts a genuine new attempt.
 */
export function loadMapboxGl(): Promise<typeof mapboxgl> {
  if (typeof window === 'undefined') return NEVER_IN_THIS_CONTEXT;

  if (glPromise) return glPromise;

  installOnlineRecovery();

  glPromise = withRetry(
    () =>
      withTimeout(
        Promise.all([loadScript(GL_JS), loadStylesheet(GL_CSS)]).then(() => {
          const gl = (window as any).mapboxgl as typeof mapboxgl | undefined;
          if (!gl) {
            forgetAsset(GL_JS);
            throw new MapboxContractError(
              '[mapbox] mapbox-gl.js loaded but did not define window.mapboxgl'
            );
          }
          return gl;
        }),
        'mapbox-gl.js'
      ),
    'mapbox-gl.js',
    'mapboxgl'
  )
    .then((gl) => {
      (gl as any).accessToken = environment.mapbox.accessToken;
      return gl;
    })
    .catch((error) => {
      reportLoadFailure(
        `[mapbox] GL JS failed to load after ${describeAttempts(error)}, maps cannot render`,
        error
      );
      hasFailed = true;
      glPromise = null;
      throw error;
    });

  return glPromise;
}

/**
 * Resolves with the global `MapboxGeocoder`. Depends on GL JS: the plugin is
 * handed `mapboxgl` for its marker, and its stylesheet assumes mapbox-gl.css.
 */
export function loadMapboxGeocoder(): Promise<typeof MapboxGeocoder> {
  if (geocoderPromise) return geocoderPromise;

  geocoderPromise = loadMapboxGl()
    .then(() =>
      withRetry(
        () =>
          withTimeout(
            Promise.all([
              loadScript(GEOCODER_JS),
              loadStylesheet(GEOCODER_CSS),
            ]).then(() => {
              const geocoder = (window as any).MapboxGeocoder as
                | typeof MapboxGeocoder
                | undefined;
              if (!geocoder) {
                forgetAsset(GEOCODER_JS);
                throw new MapboxContractError(
                  '[mapbox] mapbox-gl-geocoder loaded but did not define window.MapboxGeocoder'
                );
              }
              return geocoder;
            }),
            'mapbox-gl-geocoder.js'
          ),
        'mapbox-gl-geocoder.js',
        'MapboxGeocoder'
      )
    )
    .catch((error) => {
      reportLoadFailure(
        `[mapbox] geocoder plugin failed to load after ${describeAttempts(error)}, address search is unavailable`,
        error
      );
      hasFailed = true;
      geocoderPromise = null;
      throw error;
    });

  return geocoderPromise;
}

/**
 * True once the global exists - note that is a moment earlier than
 * `loadMapboxGl()` resolving, which also sets `accessToken`.
 *
 * For the handful of synchronous accessors that are only ever reached from a
 * map that has already been built, so they can say so loudly instead of
 * throwing a bare TypeError.
 *
 * The `typeof` covers the property, not the identifier: `window` itself is
 * undefined during a server render, so it needs its own test. Unreachable
 * there today - the loaders never resolve off-browser, so no map is ever
 * built - but the answer is "no" either way, and a false that means "no map
 * here" is the same thing a caller is already prepared for.
 */
export function isMapboxGlLoaded(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof (window as any).mapboxgl !== 'undefined'
  );
}

/** As `isMapboxGlLoaded`, for the geocoder plugin's own global. */
export function isMapboxGeocoderLoaded(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof (window as any).MapboxGeocoder !== 'undefined'
  );
}

/**
 * Fire-and-forget warm-up, called from `loadComponent` in the routing table so
 * the CDN fetch runs concurrently with the route chunk download rather than
 * after it. Rejections are already logged inside the loaders; swallow them
 * here so an unhandled rejection never reaches the console twice.
 */
export function preloadMapbox(options?: { geocoder?: boolean }): void {
  // No platform guard here on purpose: both loaders below own it, which also
  // covers the callers that reach them without passing through this function.
  const work = options?.geocoder ? loadMapboxGeocoder() : loadMapboxGl();
  work.catch(() => {});
}

/**
 * WebGL check, kept next to the loader so every map entry point asks the same
 * question. `mapboxgl.supported()` covers "no WebGL at all" and "WebGL that is
 * too slow to be usable", both of which otherwise surface as an empty grey box.
 *
 * Unlike a load failure this is a property of the browser, so it is never
 * worth retrying - map entry points offer no retry when this is what failed.
 *
 * console.warn and not console.error on purpose: the visitor is already told
 * in the map container, and captureConsoleIntegration only reports the error
 * level. A machine with no usable GPU is not a defect anyone can fix from
 * here, so it does not belong in Sentry.
 */
export function isMapboxSupported(gl: typeof mapboxgl): boolean {
  const supported = (gl as any).supported?.();
  if (supported === false) {
    console.warn(
      '[mapbox] WebGL is unavailable or unusable in this browser, the map cannot render'
    );
    return false;
  }
  return true;
}

/** True when a Map construction failure is the browser refusing a context. */
function isWebGlFailure(error: unknown): boolean {
  return /webgl/i.test((error as Error)?.message ?? '');
}

/**
 * `mapboxgl.supported()` answering true is not a promise that the *next*
 * `getContext()` call succeeds. A page that already holds several live
 * contexts, a GPU reset, a driver that fails on the real canvas after passing
 * on the probe one - each makes the Map constructor throw
 * `Failed to initialize WebGL.` synchronously, which is exactly what Sentry
 * issue MAP-AI-FRONTEND-1VM is.
 *
 * Left uncaught that throw abandons the caller mid-setup, so the container
 * keeps shimmering with no message, and it reaches Sentry twice: once from GL
 * JS console.erroring an `error` event nobody has subscribed to yet, once as
 * the uncaught exception.
 *
 * Returns null when the browser could not give the map a context, so the
 * caller can show the same non-retryable message as a failed `supported()`
 * check. Any other construction failure is a bug of ours and is rethrown.
 */
export function createMapboxMap<T>(construct: () => T): T | null {
  try {
    return construct();
  } catch (error) {
    if (!isWebGlFailure(error)) throw error;
    console.warn(
      '[mapbox] the browser refused a WebGL context, the map cannot render',
      error
    );
    return null;
  }
}
